import { useEffect, useState } from "react";
import { api, type PlanInfo, type PlanDef } from "../../lib/api";

export default function Billing() {
  const [current, setCurrent] = useState<PlanInfo | null>(null);
  const [plans, setPlans] = useState<PlanDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refreshPlans = () =>
    api.billing.plans().then((r) => {
      setCurrent(r.current);
      setPlans(r.plans);
    });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromPortal = params.get("from_portal") === "1";

    const load = async () => {
      if (fromPortal) {
        // Auto-sync so the page reflects any plan changes made in the portal
        try {
          await api.billing.sync();
        } catch {
          // Best-effort — fall through to normal load
        }
        // Remove the param from the URL without a page reload
        const clean = window.location.pathname;
        window.history.replaceState({}, "", clean);
      }
      await refreshPlans();
    };

    load()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    try {
      const { url } = await api.billing.checkout(planId);
      window.location.assign(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to start checkout");
      setSubscribing(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.billing.portal();
      window.location.assign(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to open billing portal");
      setPortalLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.billing.sync();
      await refreshPlans();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleBuyTokens = async () => {
    setTokenLoading(true);
    try {
      const { url } = await api.billing.buyTokens();
      window.location.assign(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to start token purchase");
      setTokenLoading(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;

  // User has an active paid subscription (solo / business / agency)
  const hasActiveSub = current?.plan && current.plan !== "free";

  // Plans available for new/lapsed subscribers — never show Free
  const paidPlans = plans.filter((p) => p.id !== "free");

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
      </div>

      {/* ── Active subscription view ───────────────────────────────────────── */}
      {hasActiveSub && (
        <>
          {/* Current plan card */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                  Current plan
                </p>
                <p className="text-2xl font-bold text-gray-900 capitalize">
                  {current?.plan}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {current?.status === "trialing" && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                      Trial
                    </span>
                  )}
                  {current?.status === "active" && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Active
                    </span>
                  )}
                  {current?.status === "past_due" && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                      Payment past due
                    </span>
                  )}
                  {current?.interval === "year" && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Annual billing
                    </span>
                  )}
                  {current?.interval === "month" && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      Monthly billing
                    </span>
                  )}
                </div>
                {current?.periodEnd && (
                  <p className="text-xs text-gray-400 mt-2">
                    {current.status === "trialing"
                      ? "Trial ends"
                      : "Next billing date"}
                    :{" "}
                    {new Date(current.periodEnd).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end gap-2 shrink-0">
                <button
                  onClick={handlePortal}
                  disabled={portalLoading}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {portalLoading ? "Redirecting…" : "Manage subscription →"}
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
                >
                  {syncing ? "Syncing…" : "Sync with Stripe"}
                </button>
              </div>
            </div>
          </div>

          {/* AI Token balance — Business plan only */}
          {current?.plan === "business" && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-0.5">
                  AI Tokens
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {(current.aiTokensBalance ?? 0).toLocaleString()}
                  <span className="text-sm font-normal text-gray-400 ml-1">
                    remaining
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  1 token = 1 AI generation run · $10 per 1,000 tokens
                </p>
              </div>
              <button
                onClick={handleBuyTokens}
                disabled={tokenLoading}
                className="btn-primary shrink-0 text-sm px-5 py-2.5 disabled:opacity-50"
              >
                {tokenLoading ? "Redirecting…" : "Buy 1,000 tokens — $10"}
              </button>
            </div>
          )}

          {/* Agency — unlimited AI callout */}
          {current?.plan === "agency" && (
            <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-5 py-4">
              <p className="text-sm font-semibold text-green-800 mb-0.5">
                Unlimited AI
              </p>
              <p className="text-sm text-green-700">
                Your Agency plan includes unlimited AI generation runs and team
                seats — no tokens needed.
              </p>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            To upgrade, downgrade, or cancel, use the{" "}
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="underline hover:text-gray-600 disabled:opacity-50"
            >
              Stripe billing portal
            </button>
            . If plan options don't appear in the portal, ask your admin to
            enable plan switching in{" "}
            <a
              href="https://dashboard.stripe.com/settings/billing/portal"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              Stripe Dashboard → Settings → Customer portal
            </a>
            .
          </p>
        </>
      )}

      {/* ── No active subscription — show plan picker ─────────────────────── */}
      {!hasActiveSub && (
        <>
          {current?.trialEligible ? (
            <div className="mb-6 rounded-xl border border-brand-200 bg-brand-50 px-5 py-4">
              <p className="text-sm font-semibold text-brand-800 mb-1">
                Start your free trial
              </p>
              <p className="text-sm text-brand-700">
                Choose a plan to begin your <strong>14-day free trial</strong>.
                No credit card required — cancel any time before it ends and you
                won't be charged.
              </p>
            </div>
          ) : (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
              <p className="text-sm font-semibold text-amber-800 mb-1">
                No active subscription
              </p>
              <p className="text-sm text-amber-700">
                Your subscription has ended. Choose a plan below to reactivate —
                billing starts immediately.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paidPlans.map((plan) => (
              <div key={plan.id} className="card p-6 flex flex-col">
                <h2 className="text-lg font-bold text-gray-900">{plan.name}</h2>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  ${plan.price}
                  <span className="text-sm font-normal text-gray-400">/mo</span>
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-gray-600 flex-1">
                  <li>
                    ✓{" "}
                    {plan.runsPerMonth === -1
                      ? "Unlimited AI generation runs"
                      : plan.id === "business"
                        ? "AI tokens add-on ($10 / 1,000)"
                        : `${plan.runsPerMonth} AI generation runs / mo`}
                  </li>
                  <li>
                    ✓{" "}
                    {plan.brands === -1
                      ? "Unlimited brands"
                      : `${plan.brands} brand${plan.brands > 1 ? "s" : ""}`}
                  </li>
                  <li>✓ All platforms</li>
                  {(plan.teamSeats === -1 || plan.teamSeats > 0) && (
                    <li>
                      ✓{" "}
                      {plan.teamSeats === -1
                        ? "Unlimited team seats"
                        : `${plan.teamSeats} team seat${plan.teamSeats > 1 ? "s" : ""}`}
                    </li>
                  )}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={!!subscribing}
                  className="btn-primary w-full mt-4 text-sm"
                >
                  {subscribing === plan.id
                    ? "Redirecting…"
                    : current?.trialEligible
                      ? `Start free trial — ${plan.name}`
                      : `Subscribe — ${plan.name}`}
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-6 text-center">
            Annual billing saves 20%. All paid plans include a 14-day free
            trial. No credit card required to start.
          </p>
        </>
      )}
    </div>
  );
}
